import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Menu, X } from "lucide-react";
import logo from '../../assets/logos/logo.png';
import { CONTACT_PHONE, CONTACT_WHATSAPP } from "../../constants/data";

const GOLD = "#50C878";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  function handleSearch(e) {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/properties?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
      setSearchOpen(false);
    }
  }

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled && !menuOpen;

  const navLinks = [
    { label: "Home", path: "/" },
    // { label: "About", path: "/about" },
    { label: "Why GroupBuy", path: "/why-groupbuy" },
    // { label: "Why Developers", path: "/why-developers" },
    // { label: "Services", path: "/services" },
    { label: "Projects", path: "/properties" },
    { label: "Developers OnBoard", path: "/developers" },
    // { label: "Careers", path: "/careers" },
    { label: "Contact", path: "/contact" },
  ];

  return (
    <>
      <nav
        className="gbf-navbar"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: transparent
            ? "linear-gradient(to bottom, rgba(247,244,236,0.92), rgba(247,244,236,0.55))"
            : "rgba(251,249,243,0.97)",
          backdropFilter: "blur(12px)",
          borderBottom: transparent ? "1px solid rgba(80,200,120,0.18)" : "1px solid rgba(80,200,120,0.22)",
          boxShadow: transparent ? "none" : "0 4px 20px rgba(1,50,32,0.06)",
          transition: "background 0.3s, border 0.3s",
          padding: "0 24px",
        }}
      >
        <div
          className="gbf-navbar-inner"
          style={{
            maxWidth: "1280px",
            margin: "0 auto",
            height: "68px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "24px",
          }}
        >
          {/* Logo */}
          <Link to="/" className="gbf-logo-link" style={{ textDecoration: "none", flexShrink: 0, minWidth: 0 }}>
            <img 
              src={logo} 
              alt="GroupBuy Flats" 
              className="gbf-logo-img"
              style={{
                height: "55px",
                width: "250px",
                display: "block",
                maxWidth: "100%",
              }}
            />
          </Link>

          {/* Desktop Nav */}
          <div style={{ display: "flex", alignItems: "center", gap: "22px", flex: 1, justifyContent: "center" }}
            className="desktop-nav"
          >
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "0.84rem",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  color: location.pathname === link.path ? GOLD : "rgba(1,50,32,0.95)",
                  textDecoration: "none",
                  letterSpacing: "0.04em",
                  borderBottom: location.pathname === link.path ? `1.5px solid ${GOLD}` : "1.5px solid transparent",
                  paddingBottom: "2px",
                  transition: "color 0.2s",
                }}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="gbf-navbar-right" style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
            {/* Search */}
            {searchOpen ? (
              <form onSubmit={handleSearch} className="gbf-search-form" style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                <input
                  autoFocus
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search properties, areas..."
                  className="gbf-search-input"
                  style={{
                    background: "rgba(80,200,120,0.10)",
                    border: `1px solid ${GOLD}`,
                    borderRadius: "20px",
                    padding: "7px 16px",
                    color: "#013220",
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.85rem",
                    outline: "none",
                    width: "220px",
                    minWidth: 0,
                  }}
                />
                <button type="submit" style={{ background: "none", border: "none", cursor: "pointer", color: GOLD, marginLeft: "6px", display: "flex", flexShrink: 0 }}>
                  <Search size={18} />
                </button>
                <button type="button" onClick={() => setSearchOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(1,50,32,0.55)", display: "flex", flexShrink: 0 }}>
                  <X size={16} />
                </button>
              </form>
            ) : (
              <button
                onClick={() => setSearchOpen(true)}
                aria-label="Search"
                style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(1,50,32,0.85)", display: "flex", padding: "6px", flexShrink: 0 }}
              >
                <Search size={20} />
              </button>
            )}

            {/* WhatsApp CTA — desktop (icon button) */}
            <a
              href={CONTACT_WHATSAPP}
              target="_blank"
              rel="noopener noreferrer"
              className="desktop-nav"
              aria-label="WhatsApp Us"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#25D366",
                color: "#013220",
                textDecoration: "none",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                flexShrink: 0,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            </a>

            {/* Hamburger */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="mobile-menu-btn"
              aria-label="Toggle menu"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "rgba(1,50,32,0.95)",
                display: "none",
                padding: "6px",
                flexShrink: 0,
              }}
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div
            className="gbf-mobile-menu"
            style={{
              background: "rgba(251,249,243,0.99)",
              borderTop: "1px solid rgba(80,200,120,0.22)",
              padding: "16px 0 24px",
              maxHeight: "calc(100vh - 68px)",
              overflowY: "auto",
            }}
          >
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                style={{
                  display: "block",
                  padding: "12px 24px",
                  color: location.pathname === link.path ? GOLD : "rgba(1,50,32,0.95)",
                  textDecoration: "none",
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "1rem",
                  fontWeight: 500,
                  borderLeft: location.pathname === link.path ? `3px solid ${GOLD}` : "3px solid transparent",
                }}
              >
                {link.label}
              </Link>
            ))}
            <a
              href={CONTACT_WHATSAPP}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                margin: "12px 24px 0",
                background: "#25D366",
                color: "#013220",
                textDecoration: "none",
                padding: "12px 20px",
                borderRadius: "6px",
                fontWeight: 700,
                fontSize: "1rem",
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp {CONTACT_PHONE}
            </a>
          </div>
        )}
      </nav>

      {/* Spacer so page content doesn't sit under the fixed navbar.
          Only needed on routes/layouts that don't already offset for it —
          harmless if a parent layout already handles this. */}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap');

        @media (max-width: 1100px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: flex !important; }
        }

        /* ============ TABLET (≤ 900px) ============ */
        @media (max-width: 900px) {
          .gbf-navbar { padding: 0 18px !important; }
          .gbf-navbar-inner { height: 60px !important; gap: 12px !important; }
          .gbf-logo-img { height: 42px !important; width: auto !important; max-width: 170px !important; }
        }

        /* ============ MOBILE (≤ 640px) ============ */
        @media (max-width: 640px) {
          .gbf-navbar { padding: 0 14px !important; }
          .gbf-navbar-inner { height: 56px !important; gap: 8px !important; }
          .gbf-logo-img { height: 34px !important; width: auto !important; max-width: 130px !important; }
          .gbf-navbar-right { gap: 6px !important; }

          /* Search input shouldn't force horizontal scroll on small screens */
          .gbf-search-form { flex: 1 1 auto !important; max-width: 240px !important; }
          .gbf-search-input { width: 100% !important; flex: 1 1 auto !important; padding: 6px 12px !important; font-size: 0.8rem !important; }

          .gbf-mobile-menu a { font-size: 0.95rem !important; padding: 11px 18px !important; }
        }

        /* ============ SMALL MOBILE (≤ 360px) ============ */
        @media (max-width: 360px) {
          .gbf-logo-img { max-width: 108px !important; height: 30px !important; }
          .gbf-search-form { max-width: 160px !important; }
        }
      `}</style>
    </>
  );
}